import { getTokenBg } from "../lib/token-colors";

interface TokenIconProps {
  symbol: string;
  size?: 14 | 16 | 18 | 20 | 24 | 28 | 32;
}

/** Inline SVG token logos for the 6 known devenv tokens with colored-circle fallback. */
export function TokenIcon({ symbol, size = 24 }: TokenIconProps) {
  const upper = symbol.toUpperCase();
  const svg = TOKEN_SVGS[upper];

  if (svg) {
    return (
      <span
        className="inline-flex shrink-0 items-center justify-center"
        style={{ width: size, height: size }}
        title={upper}
      >
        <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
          {svg}
        </svg>
      </span>
    );
  }

  // Fallback: colored circle with first letter
  const color = getTokenBg(upper);
  const letter = upper[0] ?? "?";
  const fontSize =
    size <= 16 ? 8 : size <= 20 ? 10 : size <= 24 ? 12 : size <= 28 ? 13 : 14;

  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-mono font-bold text-white"
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        fontSize,
        lineHeight: 1,
      }}
      title={upper}
    >
      {letter}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Token SVG definitions
// ---------------------------------------------------------------------------

const TOKEN_SVGS: Record<string, React.ReactNode> = {
  ETH: (
    <>
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <path
        d="M16 4l-0.2 0.7v15.5l0.2 0.2 7-4.1z"
        fill="rgba(255,255,255,0.6)"
      />
      <path d="M16 4l-7 12.3 7 4.1z" fill="#fff" />
      <path
        d="M16 22l-0.1 0.1v5.4l0.1 0.3 7-9.9z"
        fill="rgba(255,255,255,0.6)"
      />
      <path d="M16 27.8v-5.8l-7-4.1z" fill="#fff" />
      <path d="M16 20.4l7-4.1-7-3.2z" fill="rgba(255,255,255,0.2)" />
      <path d="M9 16.3l7 4.1v-7.3z" fill="rgba(255,255,255,0.5)" />
    </>
  ),
  WETH: (
    <>
      <circle cx="16" cy="16" r="16" fill="#8B7EEA" />
      <circle
        cx="16"
        cy="16"
        r="13"
        fill="none"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth="1.5"
      />
      <path d="M16 6l-0.2 0.6v13l0.2 0.2 6-3.5z" fill="rgba(255,255,255,0.6)" />
      <path d="M16 6l-6 10.3 6 3.5z" fill="#fff" />
      <path
        d="M16 21.3l-0.1 0.1v4.2l0.1 0.2 6-8.3z"
        fill="rgba(255,255,255,0.6)"
      />
      <path d="M16 25.8v-4.5l-6-3.5z" fill="#fff" />
    </>
  ),
  USDC: (
    <>
      <circle cx="16" cy="16" r="16" fill="#2775CA" />
      <path
        d="M20.2 18.4c0-2-1.2-2.7-3.6-3-1.7-.3-2-.8-2-1.6s.6-1.3 1.8-1.3 1.9.5 2 1.4h1.8c-.2-1.7-1.3-2.8-3-3.1V9h-2v1.8c-1.9.3-3.1 1.5-3.1 3.1 0 2 1.2 2.7 3.6 3 1.6.3 2 .8 2 1.7s-.8 1.4-1.9 1.4-2.2-.6-2.3-1.6H12c.1 1.8 1.4 3 3.2 3.2V23h2v-1.7c1.9-.3 3-1.5 3-3z"
        fill="#fff"
      />
    </>
  ),
  USDT: (
    <>
      <circle cx="16" cy="16" r="16" fill="#26A17B" />
      <path
        d="M17.9 17v-.1c-.1 0-1-.1-2.8-.1s-2.5.1-2.8.1v.1c-3 .1-5.2.7-5.2 1.3s2.2 1.2 5.2 1.3v4.2h3.6v-4.2c3-.1 5.2-.7 5.2-1.3s-2.3-1.2-5.2-1.3z"
        fill="#fff"
      />
      <path
        d="M17.9 16.6v-.1c0-.7 0-1.2-.1-1.7h4.2V12H10v2.8h4.2c0 .5-.1 1.1-.1 1.7v.1c-3.4.2-5.9.8-5.9 1.6s2.5 1.4 5.9 1.6v5.7h3.6v-5.7c3.4-.2 5.9-.8 5.9-1.6s-2.5-1.4-5.8-1.6z"
        fill="#fff"
      />
    </>
  ),
  DAI: (
    <>
      <circle cx="16" cy="16" r="16" fill="#F5AC37" />
      <path d="M16 7l-7.5 9L16 25l7.5-9z" fill="#fff" fillOpacity="0.85" />
      <text
        x="16"
        y="18.5"
        textAnchor="middle"
        fill="#F5AC37"
        fontFamily="Arial,sans-serif"
        fontWeight="bold"
        fontSize="10"
      >
        D
      </text>
    </>
  ),
  WBTC: (
    <>
      <circle cx="16" cy="16" r="16" fill="#F09242" />
      <circle
        cx="16"
        cy="16"
        r="12"
        fill="#F09242"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth="1"
      />
      <text
        x="16"
        y="20"
        textAnchor="middle"
        fill="#fff"
        fontFamily="Arial,sans-serif"
        fontWeight="bold"
        fontSize="13"
      >
        B
      </text>
    </>
  ),
};
